import { useEffect, useMemo, useState } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";
import { useRouter } from "next/router";
import toast from "react-hot-toast";

import CellEntityAvatar from "../CellEntityAvatar";
import ConfirmIncomingTxModal from "../ConfirmIncomingTxModal";
import TransactionComments from "../TransactionComments";
import { ArrowIn, ArrowInBoth, ArrowOut, CellEntity, CellEntityContainer, CellEntityTitle } from "../TransactionRow";

import { trackAction } from "~/analytics";
import * as Grid from "~/components/Grid";
import Loading from "~/components/Loading";
import * as ModalDeprecated from "~/components/ModalDeprecated";
import * as Tabs from "~/components/Tabs";
import { postTxUpdate, useTxInfo } from "~/endpoints/transactions";
import { Transaction, TransactionStatus } from "~/endpoints/transactions/types";
import { useTfSimpleVaspInfo } from "~/endpoints/trustFramework";
import useCurrentViewerVaspDID from "~/hooks/useCurrentViewerVaspDID";
import DDQView from "~/templates/forms/DDQForm/DDQView";
import VaspInfoSections from "~/templates/network/components/VaspPublicInfo/VaspInfoSections";
import TransactionDetailsInfo from "~/templates/transactions/components/TransactionDetailsInfo";
import { removeQueryParam } from "~/utils/url";

type TransactionModalProps = {
  onBlockchainAddressConfirmed: () => void;
  onClickOpenAccept?: (id: string) => void;
  onClickOpenDecline?: (id: string) => void;
  onClickOpenApprove?: (id: string) => void;
  onClickOpenCancel?: (id: string) => void;
};

/**
 * Similar to the modal from 'outgoing' transactions
 */
export default function TransactionDetailsModal({
  onBlockchainAddressConfirmed,
  onClickOpenAccept,
  onClickOpenDecline,
  onClickOpenApprove,
  onClickOpenCancel,
  ...props
}: TransactionModalProps) {
  const router = useRouter();
  const { query } = router;
  const transactionId = query.transaction_id_details as string;

  const { transactionCommentsEnabled } = useFlags();

  const { data: transaction, mutate: refreshTransactionDetails } = useTxInfo(transactionId);

  const [savingComment, setSavingComment] = useState(false);

  const viewerVaspDID = useCurrentViewerVaspDID();
  const [isBlockchainAddressConfirmationNeeded, setIsBlockchainAddressConfirmationNeeded] = useState<
    boolean | undefined
  >(undefined);
  const [tab, setTab] = useState<"details" | "company" | "comments">("details");

  const { data: originatorVASP, isLoading: loadingOriginatorVASP } = useTfSimpleVaspInfo(
    transaction && transaction.originatorVASPdid,
    ["*"],
    undefined,
    true,
  ); //TODO: check transactionRow for better performance
  const { data: beneficiaryVASP, isLoading: loadingBeneficiaryVASP } = useTfSimpleVaspInfo(
    transaction && transaction.beneficiaryVASPdid,
    ["*"],
    undefined,
    true,
  );

  if (transaction) {
    transaction.originatorVASP = originatorVASP;
    transaction.beneficiaryVASP = beneficiaryVASP;
  }

  let direction = originatorVASP?.did === viewerVaspDID ? "outgoing" : "incoming";
  direction = beneficiaryVASP?.did === originatorVASP?.did ? "internal" : direction;
  const counterVASP = direction === "outgoing" ? beneficiaryVASP : originatorVASP;
  const logo = counterVASP?.logo || "/assets/vasp-fallback.png";
  const isOutgoing = direction === "outgoing";
  const isIncoming = direction === "incoming";
  const isInternal = direction === "internal";

  const originatorDid = transaction?.originatorDid;
  const beneficiaryDid = transaction?.beneficiaryDid;

  useEffect(() => {
    if (transaction) {
      const trackProps = { group_vasp: viewerVaspDID };
      trackAction("user-viewed-transaction-details", trackProps);
      setIsBlockchainAddressConfirmationNeeded(transaction.status === TransactionStatus.SENT && isIncoming);
    }
  }, [transaction, viewerVaspDID, isIncoming]);

  const onCloseDetails = () => {
    removeQueryParam("transaction_id_details", router);
  };

  function handleTabClickDetails() {
    setTab("details");
  }
  function handleTabClickCompany() {
    setIsDDQVisible(false);
    setTab("company");
  }
  function handleTabClickComments() {
    setIsDDQVisible(false);
    setTab("comments");
  }

  const handleOnAddressConfirmed = (updatedTransaction: Transaction) => {
    onBlockchainAddressConfirmed();

    setIsBlockchainAddressConfirmationNeeded(false);
    const wasTransactionAutoAccepted = updatedTransaction.status === TransactionStatus.ACCEPTED;
    const wasTransactionAutoDeclined = updatedTransaction.status === TransactionStatus.DECLINED;
    if (wasTransactionAutoAccepted) {
      const trackProps = { group_vasp: viewerVaspDID };
      toast.success("Transfer was auto-accepted and moved to COMPLETED");
      trackAction("transaction-was-auto-accepted", trackProps);
    }
    if (wasTransactionAutoDeclined) {
      const trackProps = { group_vasp: viewerVaspDID };
      toast.success("Transfer was auto-declined and moved to COMPLETED");
      trackAction("transaction-was-auto-declined", trackProps);
    }
    refreshTransactionDetails();
  };

  const handleAddComment = async (commentText: string) => {
    setSavingComment(true);
    await postTxUpdate({
      id: transactionId,
      comment: commentText,
    });
    await refreshTransactionDetails();
    setSavingComment(false);
  };

  const [isDDQVisible, setIsDDQVisible] = useState(false);

  if (
    !transaction ||
    isBlockchainAddressConfirmationNeeded === undefined ||
    loadingOriginatorVASP ||
    loadingBeneficiaryVASP
  ) {
    return (
      <ModalDeprecated.Container active {...props}>
        <ModalDeprecated.Background onClick={onCloseDetails} />
        <Loading />
        <ModalDeprecated.Close onClick={onCloseDetails} />
      </ModalDeprecated.Container>
    );
  }

  if (isBlockchainAddressConfirmationNeeded === true) {
    return (
      <ConfirmIncomingTxModal
        transaction={transaction}
        onAddressConfirmed={(updatedTransaction) => {
          handleOnAddressConfirmed(updatedTransaction);
        }}
        onClose={onCloseDetails}
      />
    );
  }

  if (transaction && isBlockchainAddressConfirmationNeeded === false) {
    return (
      <ModalDeprecated.Container active {...props}>
        <ModalDeprecated.Background onClick={onCloseDetails} />
        <ModalDeprecated.Card sized="large">
          <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 40px)" }}>
            <ModalDeprecated.CardHead>
              <Grid.Container>
                <Grid.Columns multiline>
                  <Grid.Column mobile={12}>
                    <Grid.Columns gapless multiline={false} align="left">
                      <Grid.Column narrow>
                        {(isOutgoing || isInternal) && (
                          <CellEntity title={originatorDid} className="localize-ignore">
                            {transaction?.originatorRef || transaction.originatorDisplay}
                          </CellEntity>
                        )}
                        {isIncoming && (
                          <CellEntity title={beneficiaryDid} className="localize-ignore">
                            {transaction?.beneficiaryRef || transaction.beneficiaryDisplay}
                          </CellEntity>
                        )}
                      </Grid.Column>
                      <Grid.Column
                        narrow
                        style={{
                          display: "flex",
                          alignItems: "center",
                          margin: "0.3rem",
                        }}
                      >
                        {isOutgoing && <ArrowOut />}
                        {isIncoming && <ArrowIn />}
                        {isInternal && <ArrowInBoth />}
                      </Grid.Column>
                      <Grid.Column narrow>
                        {counterVASP && (
                          <CellEntityContainer>
                            {logo && <CellEntityAvatar src={logo} />}
                            <CellEntityTitle title={counterVASP.did} className="localize-ignore">
                              {counterVASP.name || counterVASP.did}
                            </CellEntityTitle>
                          </CellEntityContainer>
                        )}
                      </Grid.Column>
                      <Grid.Column narrow>
                        {(isOutgoing || isInternal) && (
                          <CellEntity title={beneficiaryDid} className="localize-ignore">
                            {transaction.beneficiaryDisplay}
                          </CellEntity>
                        )}
                        {isIncoming && (
                          <CellEntity title={originatorDid} className="localize-ignore">
                            {transaction.originatorDisplay}
                          </CellEntity>
                        )}
                      </Grid.Column>
                    </Grid.Columns>
                  </Grid.Column>
                  <Grid.Column mobile={12}>
                    <Tabs.Container>
                      <Tabs.Content>
                        <Tabs.Item active={tab === "details"}>
                          <a href="#" onClick={handleTabClickDetails}>
                            Transfer details
                          </a>
                        </Tabs.Item>
                        {counterVASP && (
                          <Tabs.Item active={tab === "company"}>
                            <a href="#" onClick={handleTabClickCompany}>
                              Company
                            </a>
                          </Tabs.Item>
                        )}
                        {transactionCommentsEnabled && (
                          <Tabs.Item active={tab === "comments"}>
                            <a href="#" onClick={handleTabClickComments}>
                              Comments
                            </a>
                          </Tabs.Item>
                        )}
                      </Tabs.Content>
                    </Tabs.Container>
                  </Grid.Column>
                </Grid.Columns>
              </Grid.Container>
            </ModalDeprecated.CardHead>

            {tab === "details" && (
              <ModalDeprecated.CardBody>
                <TransactionDetailsInfo
                  transaction={transaction}
                  onClickOpenApprove={onClickOpenApprove}
                  onClickOpenCancel={onClickOpenCancel}
                  onClickOpenAccept={onClickOpenAccept}
                  onClickOpenDecline={onClickOpenDecline}
                />
              </ModalDeprecated.CardBody>
            )}

            {tab === "company" && (
              <ModalDeprecated.CardBody id="transactionDetailsModal">
                {isDDQVisible && counterVASP ? (
                  <DDQView
                    isSinglePage
                    ownerVASPDID={counterVASP.did}
                    ownerVASPName={counterVASP.name || "unknown"}
                    onGoBack={() => setIsDDQVisible(false)}
                  />
                ) : (
                  <Grid.Columns multiline>
                    <VaspInfoSections
                      showMetabaseDashboard={false}
                      vasp={counterVASP}
                      onClickViewDDQ={() => {
                        const el = document.getElementById("transactionDetailsModal");
                        if (el) {
                          el.scrollTop = 0;
                        }
                        setIsDDQVisible(true);
                      }}
                    />
                  </Grid.Columns>
                )}
              </ModalDeprecated.CardBody>
            )}

            {tab === "comments" && (
              <ModalDeprecated.CardBody id="transactionComments">
                {savingComment && (
                  <div style={{ position: "absolute", height: "100%", width: "100%", top: 0, left: 0 }}>
                    <Loading />
                  </div>
                )}
                <TransactionComments transactionComments={transaction.comments} onClickAddComment={handleAddComment} />
              </ModalDeprecated.CardBody>
            )}
          </div>
        </ModalDeprecated.Card>
        <ModalDeprecated.Close onClick={onCloseDetails} />
      </ModalDeprecated.Container>
    );
  }

  return (
    <ModalDeprecated.Container active {...props}>
      <ModalDeprecated.Background onClick={onCloseDetails} />
      <Loading />
      <ModalDeprecated.Close onClick={onCloseDetails} />
    </ModalDeprecated.Container>
  );
}

export function useDate(str?: string | null) {
  return useMemo<Date | null>(() => {
    if (!str) return null;
    return new Date(str);
  }, [str]);
}
